
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { FilmFormValues } from "@/types/inventory";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";

interface FilmBasicFieldsProps {
  form: UseFormReturn<FilmFormValues>;
}

export function FilmBasicFields({ form }: FilmBasicFieldsProps) {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [brandError, setBrandError] = useState<string | null>(null);
  const [filmNameError, setFilmNameError] = useState<string | null>(null);
  const maxBrandLength = 20;
  const maxFilmNameLength = 20;
  
  const validateBrand = (value: string) => {
    if (!value.trim()) {
      setBrandError("Brand name cannot be empty");
      return false;
    }
    
    if (/[<>{}[\]\\\/]/.test(value)) {
      setBrandError("Brand name contains invalid characters");
      return false;
    }
    
    if (value.length > maxBrandLength) {
      setBrandError(`Brand name cannot exceed ${maxBrandLength} characters`);
      return false;
    }
    
    setBrandError(null);
    return true;
  };
  
  const validateFilmName = (value: string) => {
    if (!value.trim()) {
      setFilmNameError("Film name cannot be empty");
      return false;
    }
    
    if (/[<>{}[\]\\\/]/.test(value)) {
      setFilmNameError("Film name contains invalid characters");
      return false;
    }
    
    if (value.length > maxFilmNameLength) {
      setFilmNameError(`Film name cannot exceed ${maxFilmNameLength} characters`);
      return false;
    }
    
    setFilmNameError(null);
    return true;
  };
  
  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="brand"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('brand')}</FormLabel>
            <FormControl>
              <Input 
                placeholder={t('film_brand_placeholder')} 
                {...field}
                onChange={(e) => {
                  const value = e.target.value;
                  validateBrand(value);
                  field.onChange(value);
                }}
                onBlur={(e) => validateBrand(e.target.value)}
                className={`${isMobile ? "text-base" : ""} ${brandError ? "border-destructive" : ""}`}
                maxLength={maxBrandLength}
              />
            </FormControl>
            {brandError && (
              <p className="text-xs font-medium text-destructive">{brandError}</p>
            )}
            <FormDescription className="text-xs text-muted-foreground">
              {`${field.value ? field.value.length : 0}/${maxBrandLength} characters`}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <FormField
        control={form.control}
        name="film_name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('film_name')}</FormLabel>
            <FormControl>
              <Input 
                placeholder={t('film_name_placeholder')} 
                {...field}
                onChange={(e) => {
                  const value = e.target.value;
                  validateFilmName(value);
                  field.onChange(value);
                }}
                onBlur={(e) => validateFilmName(e.target.value)}
                className={`${isMobile ? "text-base" : ""} ${filmNameError ? "border-destructive" : ""}`}
                maxLength={maxFilmNameLength}
              />
            </FormControl>
            {filmNameError && (
              <p className="text-xs font-medium text-destructive">{filmNameError}</p>
            )}
            <FormDescription className="text-xs text-muted-foreground">
              {`${field.value ? field.value.length : 0}/${maxFilmNameLength} characters`}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
